import { isNumericPricing } from "@/domain/types";
import type { ExecutableDiscoveryModel, RuntimeOffering } from "@/domain/discovery-model";
import type { PricingFactor } from "@/domain/types";

type Owner = { collection: "products" | "services"; offering: RuntimeOffering };
export type PricingOperand = {
  collection: Owner["collection"];
  offeringId: string;
  factorId?: string;
};
export type PricingRule = Owner & {
  key: string;
  kind: "base" | PricingFactor["kind"];
  values: number[];
  factor?: PricingFactor;
  operand: PricingOperand;
};

/** Inventory comes from stored rules, independently of answer-local editability. */
export function pricingRules(model: ExecutableDiscoveryModel): PricingRule[] {
  return (["products", "services"] as const).flatMap((collection) =>
    model[collection].flatMap((offering) => {
      if (!isNumericPricing(offering.pricing)) return [];
      const owner = { collection, offering };
      const key = `pricing:${collection}:${offering.id}`;
      const operand = { collection, offeringId: offering.id };
      return [
        {
          ...owner,
          key: `${key}:base`,
          kind: "base" as const,
          operand,
          values:
            offering.pricing.display === "fixed"
              ? [offering.pricing.fixed!]
              : [offering.pricing.low, offering.pricing.high],
        },
        ...offering.pricing.factors.map((factor) => ({
          ...owner,
          key: `${key}:factor:${factor.id}`,
          kind: factor.kind,
          factor,
          operand: { ...operand, factorId: factor.id },
          values:
            factor.kind === "multiplier"
              ? [Number(((factor.multiplier! - 1) * 100).toFixed(10))]
              : factor.amount !== undefined
                ? [factor.amount]
                : [factor.low!, factor.high!],
        })),
      ];
    }),
  );
}

export function resolvePricingOperand(model: ExecutableDiscoveryModel, operand: PricingOperand) {
  return pricingRules(model).find(
    (r) =>
      r.operand.collection === operand.collection &&
      r.operand.offeringId === operand.offeringId &&
      r.operand.factorId === operand.factorId,
  );
}
export type PricingRelationship = Owner & {
  key: string;
  kind: "base" | "fixed" | "per-unit" | "multiplier" | "unpriced";
  values: number[];
  factor?: PricingFactor;
  questionId: string;
  optionId?: string;
};

/** Resolve rule ownership, never an aggregate or a calculated option delta. */
export function resolvePricingEdit(
  model: ExecutableDiscoveryModel,
  questionId: string,
  optionId?: string,
): PricingRelationship | null {
  const question = model.scenario.steps
    .flatMap((s) => s.questions)
    .find((q) => q.id === questionId);
  if (
    !question ||
    (question.kind === "slider"
      ? optionId !== undefined
      : !question.options.some((o) => o.id === optionId))
  )
    return null;
  const owners: Owner[] = [];
  const bases: Owner[] = [];
  let selectsOffering = false;
  for (const collection of ["products", "services"] as const) {
    const selection = model.selection[collection === "products" ? "product" : "service"];
    const reachable =
      selection.kind === "fixed"
        ? [selection.offeringId]
        : [selection.fallbackId, ...Object.values(selection.options)];
    owners.push(
      ...model[collection]
        .filter((o) => reachable.includes(o.id))
        .map((offering) => ({ collection, offering })),
    );
    if (selection.kind === "question" && selection.questionId === questionId) {
      selectsOffering = true;
      const id = optionId && selection.options[optionId];
      const offering = model[collection].find((o) => o.id === id);
      // A base shared by multiple options is not an option-local edit.
      if (!offering || Object.values(selection.options).filter((v) => v === id).length !== 1)
        return null;
      if (
        selection.fallbackId === id &&
        "options" in question &&
        question.options.some((o) => o.id !== optionId && !selection.options[o.id])
      )
        return null;
      bases.push({ collection, offering });
    }
  }
  const matches = owners.flatMap((owner) =>
    owner.offering.pricing.factors
      .filter(
        (f) =>
          f.answerId === questionId &&
          (optionId === undefined || !f.answerValues?.length || f.answerValues.includes(optionId)),
      )
      .map((factor) => ({ ...owner, factor })),
  );
  const identity = { questionId, ...(optionId === undefined ? {} : { optionId }) };
  if (bases.length) {
    if (bases.length !== 1 || matches.length) return null;
    const owner = bases[0]!;
    if (!isNumericPricing(owner.offering.pricing) || owner.offering.pricing.display === "hidden") return null;
    const p = owner.offering.pricing;
    return {
      ...owner,
      ...identity,
      key: `pricing:${owner.collection}:${owner.offering.id}:base`,
      kind: "base",
      values: p.display === "fixed" ? [p.fixed!] : [p.low, p.high],
    };
  }
  if (matches.length) {
    if (matches.length !== 1) return null;
    const match = matches[0]!;
    if (!isNumericPricing(match.offering.pricing) || match.offering.pricing.display === "hidden") return null;
    const f = match.factor;
    if (optionId !== undefined && (f.answerValues?.length !== 1 || f.kind === "per-unit"))
      return null;
    // Numeric conditions are not one rate applying across the slider.
    if (optionId === undefined && f.answerValues?.length) return null;
    // A later multiplier changes the effective monetary contribution. Avoid a misleading surcharge.
    const factors = match.offering.pricing.factors;
    if (
      f.kind !== "multiplier" &&
      factors.slice(factors.indexOf(f) + 1).some((x) => x.kind === "multiplier")
    )
      return null;
    return {
      ...match,
      ...identity,
      key: `pricing:${match.collection}:${match.offering.id}:factor:${f.id}`,
      kind: f.kind,
      values:
        f.kind === "multiplier"
          ? [Number(((f.multiplier! - 1) * 100).toFixed(10))]
          : f.amount !== undefined
            ? [f.amount]
            : [f.low!, f.high!],
    };
  }
  // Absence is editable only for a single, unconditional offering scope.
  if (selectsOffering || optionId === undefined || owners.length !== 1) return null;
  const owner = owners[0]!;
  if (!isNumericPricing(owner.offering.pricing) || owner.offering.pricing.display === "hidden") return null;
  const selection = model.selection[owner.collection === "products" ? "product" : "service"];
  if (
    selection.kind !== "fixed" ||
    owner.offering.pricing.factors.some((f) => f.kind === "multiplier")
  )
    return null;
  return {
    ...owner,
    ...identity,
    key: `pricing:${owner.collection}:${owner.offering.id}:new:${questionId}:${optionId}`,
    kind: "unpriced",
    values: [0],
  };
}

/** Applies to a fresh local clone; the original artifact and engine stay untouched. */
export function applyPricingEdit(
  model: ExecutableDiscoveryModel,
  relationship: PricingRelationship | PricingRule,
  values: number[],
) {
  if (values.length !== relationship.values.length || !values.every(Number.isFinite))
    throw new Error("Pricing operand shape cannot change.");
  const p = model[relationship.collection].find((o) => o.id === relationship.offering.id)!.pricing;
  if (!isNumericPricing(p)) throw new Error("Quote-required pricing has no numeric operands to edit.");
  if (relationship.kind === "base") {
    if (p.display === "fixed") p.fixed = values[0]!;
    else {
      p.low = values[0]!;
      p.high = values[1]!;
    }
    return;
  }
  if (relationship.kind === "unpriced") {
    let id = `local-${relationship.questionId}-${relationship.optionId}`;
    while (p.factors.some((f) => f.id === id)) id += "-new";
    const q = model.scenario.steps
      .flatMap((s) => s.questions)
      .find((q) => q.id === relationship.questionId)!;
    const label =
      "options" in q ? q.options.find((o) => o.id === relationship.optionId)!.label : q.title;
    p.factors.push({
      id,
      label: { ...label },
      kind: "fixed",
      answerId: relationship.questionId,
      answerValues: [relationship.optionId!],
      amount: values[0]!,
    });
    return;
  }
  const factor = p.factors.find((f) => f.id === relationship.factor!.id)!;
  if (relationship.kind === "multiplier") factor.multiplier = 1 + values[0]! / 100;
  else if (factor.amount !== undefined) factor.amount = values[0]!;
  else {
    factor.low = values[0]!;
    factor.high = values[1]!;
  }
}
